<template>
  <ModalContainer
    :is-show-modal="!!facilityObjectId"
    :is-loading="isLoading"
    :styles="{ 'facility-object-modal': true }"
    :is-disabled-teleport="isDisabledTeleport"
    @close="closeModal"
  >
    <template #header>
      <Transition name="facility-object-modal-title-slide-up" mode="out-in">
        <div
          v-if="!isModalBodyScrollThresholdPass"
          class="facility-object-modal__title facility-object-modal__title--primary"
        >
          {{ facilityData?.name }}
        </div>
        <div
          v-else
          class="facility-object-modal__title facility-object-modal__title--secondary"
        >
          {{ facilityData?.name }}
        </div>
      </Transition>
      <IconButton
        class="facility-object-modal__close"
        icon="cross"
        @click="closeModal"
      />
    </template>

    <div
      ref="modalBody"
      class="facility-object-modal__body"
      @scroll="handleScrollModalBody"
    >
      <div class="facility-object-modal__block">
        {{ facilityData?.address }}
      </div>

      <div
        class="facility-object-modal__block"
        v-if="facilityData?.imageUrls?.length"
      >
        <img :src="facilityData?.imageUrls[0]" :alt="facilityData?.name" />
      </div>

      <div
        v-if="facilityData?.description"
        class="facility-object-modal__block"
      >
        <p class="facility-object-modal__block-title">
          {{ $t("About this facility") }}
        </p>
        <p v-html="facilityData.description"></p>
      </div>

      <div class="facility-object-modal__block">
        <p class="facility-object-modal__block-title">{{ $t("Check-in options") }}</p>
        <div class="facility-object-modal__block">
          <BaseSelect
            v-if="levelArray"
            styled
            arrow
            :options="levelArray"
            :model-value="selectValue"
            @change="onChangeLevel"
          >
            <template #value>
              <span class="capitalize">{{ selectValue.value }}</span>
            </template>
            <template #option="options">
              {{ options.option.value }}
            </template>
          </BaseSelect>
        </div>
        <ul v-if="selectedLevel" class="facility-object-modal__list">
          <li
            v-for="(service, index) in selectedLevel.availableServices"
            :key="`service-${index}`"
          >
            <div class="facility-object-modal__list-item">
              <span>{{ $t(service.name) }}</span>
              <BaseIcon icon="badgePlus" v-if="service.isLimited" />
            </div>
          </li>
        </ul>
      </div>

      <div
        v-if="selectedLevel && selectedLevel.limitationType !== LimitationTypeEnum.NONE"
        class="facility-object-modal__block"
      >
        <p class="facility-object-modal__block-title">{{ $t("Visit limits") }}</p>
        <span class="facility-object-modal__list">{{  $t("Current visits for plus") }}</span>
      </div>

      <ExpansionItem
        class="facility-object-modal__expansion"
        :title="$t('Additional information')"
      >
        <div class="facility-object-modal__expansion-wrapper">
          <div class="facility-object-modal__block">
            <p>
              {{ $t("Subscription type warning") }}
            </p>
          </div>

          <div class="facility-object-modal__block">
            <p class="facility-object-modal__block-title">
              {{ $t("Activities") }}
            </p>

            <ul v-if="selectedLevel" class="facility-object-modal__list">
              <li
                v-for="(activity, index) in selectedLevel?.activities"
                :key="`activity-${index}`"
              >
                {{ $t(activity) }}
              </li>
            </ul>
          </div>

          <div class="facility-object-modal__block">
            <p class="facility-object-modal__block-title">{{ $t("More") }}</p>

            <ul v-if="selectedLevel" class="facility-object-modal__list">
              <li
                v-for="(tag, index) in facilityData?.tags"
                :key="`activity-${index}`"
              >
                {{ $t(tag) }}
              </li>
            </ul>
          </div>

          <div class="facility-object-modal__block">
            <p class="facility-object-modal__block-title">
              {{ $t("Office hours") }}
            </p>
            <ul v-if="selectedLevel" class="facility-object-modal__list">
              <li
                v-for="(day, index) in facilityData?.workingTime"
                :key="`day-${index}`"
              >
                <span> {{ $t(index || "??") }}: </span>
                <span> {{ day || "??" }}</span>
              </li>
            </ul>
          </div>

          <div class="facility-object-modal__block">
            <p class="facility-object-modal__block-title">
              {{ $t("Contacts") }}
            </p>
            <ul class="facility-object-modal__list">
              <li>
                <p>{{ $t("Address") }}</p>
                <span>{{ facilityData?.address }}</span>
              </li>
              <li>
                <p>{{ $t("Website") }}</p>
                <a :href="facilityData?.url" target="_blank">{{
                  facilityData?.url
                }}</a>
              </li>
              <li class="facility-object-modal__phone-block">
                <p>{{ $t("Phone") }}</p>
                <div>
                  <a
                    v-for="phone in facilityData?.phones"
                    :key="phone"
                    :href="`tel:${phone}`"
                  >
                    {{ phone }}</a
                  >
                </div>
              </li>
            </ul>
          </div>
        </div>
      </ExpansionItem>
    </div>
  </ModalContainer>
</template>

<script setup lang="ts">
import { PropType } from "vue";
import ModalContainer from "~/components/modals/ModalContainer.vue";
import IconButton from "~/components/button/IconButton.vue";
import { getGetSupplierDetails } from "~/api/supplierRequest";
import {
  ISupplierDetails,
  ISupplierLevelInfo,
} from "~/types/supplier.interface";
import BaseSelect, {
  BaseSelectOption,
} from "~/components/input/BaseSelect.vue";
import BaseIcon from "~/components/icons/BaseIcon.vue";
import { Archived } from "~/types/entities";
import ExpansionItem from "~/components/expansionItem/ExpansionItem.vue";
import { LimitationTypeEnum } from "~/server/types/server.interface";
import {
  ARCHIVED_SUBSCRIPTION_MODEL_ID,
  DEFAULT_SUBSCRIPTION_MODEL_ID,
  mapSubscriptionModelToLevels
} from "~/constants";
import type { SeparatedLevel } from "~/types/map.interface";

const MODAL_BODY_SCROLL_THRESHOLD = 50;

const emit = defineEmits(["close", "update:isModalBodyScrollThresholdPass"]);

const props = defineProps({
  facilityObjectId: {
    type: [Number, null, String] as PropType<number | null | string>,
    required: true,
  },
  selectedLevel: {
    type: Object as PropType<SeparatedLevel>,
    required: true,
  },
  subscriptionModel: {
    type: Number as PropType<number>,
    required: true,
  },
  tabOptions: {
    type: Object as PropType<BaseSelectOption[]>,
    required: true,
  },
  isDisabledTeleport: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  isModalBodyScrollThresholdPass: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
});

const facilityData = ref<ISupplierDetails | null>(null);
const facilityDataByType = {
  archived: {} as ISupplierDetails,
  default: {} as ISupplierDetails,
};

const isLoading = ref<boolean>(false);

const modalBody = ref<HTMLDivElement | null>(null);

const localSelectedSubscriptionModel = ref<number | null>(null);
const aggregatedSubscriptionModel = computed(
  () => localSelectedSubscriptionModel.value === null ? props.subscriptionModel : localSelectedSubscriptionModel.value
);

const levelTypeBySubscriptionModel = computed(() => mapSubscriptionModelToLevels[aggregatedSubscriptionModel.value]);
const storeLevelId = computed(() => props.selectedLevel[levelTypeBySubscriptionModel.value].id);
const levelArray = computed(() =>
  facilityData.value?.levels.map((e) => {
    return { id: e.id, value: e.value };
  })
);

const selectedLevel = ref<ISupplierLevelInfo>();
const selectValue = computed(() => {
  return {
    id: selectedLevel.value?.id || "",
    value: selectedLevel.value?.value || "",
  };
});

function onChangeLevel(value: BaseSelectOption) {
  return selectedLevel.value = facilityData.value?.levels.find(
    (e) => e.id === value.id
  );
}

function closeModal() {
  emit("close");
}

function handleScrollModalBody() {
  if (!modalBody.value) {
    return;
  }

  const isThresholdPass =
    modalBody.value.scrollTop > MODAL_BODY_SCROLL_THRESHOLD;

  emit("update:isModalBodyScrollThresholdPass", isThresholdPass);
}

watch(
  () => props.facilityObjectId,
  async () => {
    if (props.facilityObjectId) {
      isLoading.value = true;
      const [defaultData, archivedData] = await Promise.all([
        getGetSupplierDetails(
          props.facilityObjectId.toString(),
          DEFAULT_SUBSCRIPTION_MODEL_ID
        ),
        getGetSupplierDetails(
          props.facilityObjectId.toString(),
          ARCHIVED_SUBSCRIPTION_MODEL_ID
        )
      ]);
      facilityDataByType.default = defaultData;
      facilityDataByType.archived = archivedData;
      facilityData.value = facilityDataByType[levelTypeBySubscriptionModel.value];

      if (levelArray.value) {
        const defaultLevel =
          (storeLevelId.value &&
            levelArray.value?.find((e) => e.id === storeLevelId.value)) ||
          levelArray.value?.find((e) => e.id === Archived.PLATINUM) ||
          levelArray.value[0];
        onChangeLevel(defaultLevel);
      }

      isLoading.value = false;
    }
  },
  { immediate: true }
);

</script>
